import { ThemeEvents } from '@theme/events';

const TABBY_SCRIPT_URL = 'https://checkout.tabby.ai/tabby-promo.js';
const SUPPORTED_CURRENCIES = new Set(['AED', 'SAR', 'KWD']);

/** @type {Promise<void> | null} */
let tabbyScriptPromise = null;

/**
 * @param {number} priceCents
 * @param {string} currency
 * @returns {string}
 */
function formatTabbyPrice(priceCents, currency) {
  const upperCurrency = currency.toUpperCase();
  const decimals = upperCurrency === 'KWD' ? 3 : 2;
  const divisor = Math.pow(10, decimals);
  return (priceCents / divisor).toFixed(decimals);
}

function loadTabbyPromoScript() {
  if (typeof window.TabbyPromo === 'function') {
    return Promise.resolve();
  }

  if (!tabbyScriptPromise) {
    tabbyScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${TABBY_SCRIPT_URL}"]`);
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Tabby promo script failed to load')), {
          once: true,
        });
        return;
      }

      const script = document.createElement('script');
      script.src = TABBY_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Tabby promo script failed to load'));
      document.head.appendChild(script);
    });
  }

  return tabbyScriptPromise;
}

const TABBY_BORDERLESS_SHADOW_CSS = `
  :host {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    background: transparent !important;
  }

  :host *,
  :host *::before,
  :host *::after {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }
`;

/**
 * Tabby renders inside shadow roots — page CSS cannot reach it.
 * @param {ParentNode} root
 */
function stripTabbyBordersInRoot(root) {
  if (root instanceof ShadowRoot && !root.querySelector('style[data-tabby-borderless]')) {
    const style = document.createElement('style');
    style.setAttribute('data-tabby-borderless', '');
    style.textContent = TABBY_BORDERLESS_SHADOW_CSS;
    root.appendChild(style);
  }

  const elements =
    root instanceof ShadowRoot || root instanceof DocumentFragment || root instanceof HTMLElement
      ? root.querySelectorAll('*')
      : [];

  elements.forEach((element) => {
    if (!(element instanceof HTMLElement)) return;

    element.style.setProperty('border', 'none', 'important');
    element.style.setProperty('box-shadow', 'none', 'important');
    element.style.setProperty('outline', 'none', 'important');

    if (element.shadowRoot) {
      stripTabbyBordersInRoot(element.shadowRoot);
    }
  });
}

/**
 * @typedef {Object} TabbyPromoConfig
 * @property {string} selector
 * @property {string} currency
 * @property {string} price
 * @property {string} lang
 * @property {string} source
 * @property {boolean} shouldInheritBg
 * @property {string} publicKey
 * @property {string} merchantCode
 */

class TabbyPromoElement extends HTMLElement {
  /** @type {(() => void) | null} */
  #variantListener = null;

  /** @type {MutationObserver | null} */
  #borderlessObserver = null;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    this.#variantListener = (event) => this.#handleVariantUpdate(event);
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.#variantListener);
    this.#renderPromo(this.#getPriceCents());
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection && this.#variantListener) {
      closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#variantListener);
    }
    this.#variantListener = null;
    this.#stopBorderlessObserver();
  }

  #stripTabbyBorders() {
    this.style.setProperty('border', 'none', 'important');
    this.style.setProperty('box-shadow', 'none', 'important');
    this.style.setProperty('outline', 'none', 'important');

    const container = this.querySelector('[data-tabby-promo-container]');
    stripTabbyBordersInRoot(this);
    if (container) stripTabbyBordersInRoot(container);

    const block = this.closest('.shopify-block');
    if (block instanceof HTMLElement) {
      block.style.setProperty('border', 'none', 'important');
      block.style.setProperty('box-shadow', 'none', 'important');
      block.style.setProperty('padding', '0', 'important');
    }
  }

  /**
   * @param {HTMLElement} container
   */
  #startBorderlessObserver(container) {
    this.#stopBorderlessObserver();

    const apply = () => this.#stripTabbyBorders();
    apply();

    [0, 50, 150, 400, 1000].forEach((delay) => {
      window.setTimeout(apply, delay);
    });

    this.#borderlessObserver = new MutationObserver(apply);
    this.#borderlessObserver.observe(container, { childList: true, subtree: true });
    this.#borderlessObserver.observe(this, { childList: true, subtree: true });
  }

  #stopBorderlessObserver() {
    if (!this.#borderlessObserver) return;
    this.#borderlessObserver.disconnect();
    this.#borderlessObserver = null;
  }

  /**
   * @param {Event} event
   */
  #handleVariantUpdate(event) {
    if (!(event instanceof CustomEvent) || !event.detail) return;

    if (event.detail.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const variant = event.detail.resource;
    if (!variant || typeof variant.price !== 'number') return;

    this.#renderPromo(variant.price);
  }

  /**
   * @returns {number}
   */
  #getPriceCents() {
    return Number(this.dataset.priceCents) || 0;
  }

  /**
   * @param {number} priceCents
   */
  async #renderPromo(priceCents) {
    const configScript = this.querySelector('[data-tabby-config]');
    const container = this.querySelector('[data-tabby-promo-container]');
    if (!(configScript instanceof HTMLScriptElement) || !(container instanceof HTMLElement)) return;

    /** @type {TabbyPromoConfig} */
    let config;
    try {
      config = JSON.parse(configScript.textContent || '{}');
    } catch {
      return;
    }

    if (!config.publicKey || !config.merchantCode || !SUPPORTED_CURRENCIES.has(config.currency?.toUpperCase())) {
      return;
    }

    config.price = formatTabbyPrice(priceCents, config.currency);
    this.dataset.priceCents = String(priceCents);

    try {
      await loadTabbyPromoScript();
    } catch {
      return;
    }

    if (typeof window.TabbyPromo !== 'function') return;

    container.innerHTML = '';
    this.#stopBorderlessObserver();
    new window.TabbyPromo(config);
    this.#startBorderlessObserver(container);
  }
}

if (!customElements.get('tabby-promo')) {
  customElements.define('tabby-promo', TabbyPromoElement);
}
